import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import _groupBy from 'lodash/groupBy';
import _startCase from 'lodash/startCase';
import type { ReactNode } from 'react';
import { Fragment, startTransition, useEffect, useMemo } from 'react';
import { Button, Icon, Menu, MenuItem, Popup } from 'semantic-ui-react';
import { useIsClient } from 'usehooks-ts';

import type { HealthStatus } from '@models';
import { navigableRouteMetadata } from '@routes/utils/nav';
import { logout } from '@store/actions/action-auth';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import ShowTour from '@components/show-tour';
import { SideNavDecorationContainer } from '@components/side-nav-decoration';
import StatusIcon, { healthStatusVariants } from '@components/status-icon';
import { useSystemHealth } from '@features/system-health/health.slice';

import {
  useHasReportService,
  useSideNavItems,
  useSideNavPopupData,
} from './hooks';
import type { SideNavContextType } from './side-nav-context';
import SideNavProvider from './side-nav-context';
import SideNavItem from './side-nav-item';
import s from './side-nav.scss';

const SystemStatusIcon = ({ status }: { status?: HealthStatus }) => (
  <span className="animate__animated animate__fadeIn">
    <Popup
      position="right center"
      trigger={
        <StatusIcon
          variant={status && healthStatusVariants[status]}
          name={status === undefined ? 'circle notched' : undefined}
          loading={!status}
        />
      }
      content={status ? `System ${status}` : `Checking system status...`}
    />
  </span>
);

export interface SideNavProps extends Pick<
  SideNavContextType,
  'activeItem' | 'isExpanded'
> {
  enrichInventoryView: boolean;
  routeMask: string;
  hash?: string;
}

/**
 * Top-level sidebar navigation component that provides the primary navigation
 * mechanism. The list of nav items is modified based on permissions and config.
 */
export const SideNav = ({
  activeItem,
  enrichInventoryView,
  isExpanded,
  routeMask,
  hash,
}: SideNavProps) => {
  useStyles(s);

  const isClient = useIsClient();

  const dispatch = useAppDispatch();

  const hasReportService = useHasReportService();

  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);

  const { status, updateHealthData } = useSystemHealth();

  const popupData = useSideNavPopupData();

  const navItems = useSideNavItems(navigableRouteMetadata, {
    system: { suffix: <SystemStatusIcon status={status} /> },
    reports: { isDisabled: !hasReportService },
    inventory: {
      isDisabled: (enrichInventoryView && !hasReportService) || undefined,
    },
  });

  useEffect(() => {
    if (!isClient) return;
    if (isAuthenticated) startTransition(() => updateHealthData());
  }, [isClient, isAuthenticated, updateHealthData]);

  const groupedNavItems = useMemo(
    () =>
      Object.entries(_groupBy(navItems, 'category')).map(
        ([key, groupItems]) => ({
          category: key === 'undefined' ? undefined : key,
          items: groupItems,
        }),
      ),
    [navItems],
  );

  if (!isAuthenticated) return null;

  return (
    <SideNavProvider value={{ activeItem, isExpanded, popupData }}>
      <aside
        className={classNames(s.root, isExpanded ? s.expanded : s.collapsed)}
      >
        <Menu
          as="nav"
          aria-label="Main"
          className={s.menu}
          vertical
          compact
          fluid
          borderless
        >
          {groupedNavItems.map(group => {
            const items: ReactNode[] = group.items.map(item => (
              <SideNavItem key={item.id} {...item} />
            ));

            if (group.category) {
              items.unshift(
                <div
                  key={`category-${group.category}`}
                  className={s.categoryHeader}
                  aria-hidden="true"
                >
                  <span>{_startCase(group.category)}</span>
                </div>,
              );
            }

            return (
              <Fragment key={group.category ?? 'uncategorized'}>
                {items}
              </Fragment>
            );
          })}

          {isExpanded && (
            <MenuItem id="show-view-tour">
              <ShowTour routeMask={routeMask} hash={hash} />
            </MenuItem>
          )}
        </Menu>

        <SideNavDecorationContainer />

        <div className={s.footer}>
          <div className={s.logoutWrapper}>
            <Popup
              className="animate__animated animate__fadeIn"
              content="Log Out"
              disabled={isExpanded}
              position="right center"
              trigger={
                <Button
                  className={classNames(
                    s.logoutButton,
                    isExpanded ? '' : 'animate__animated animate__fadeIn',
                  )}
                  onClick={() => {
                    dispatch(logout({ server: true }));
                  }}
                >
                  <Icon color="grey" name="log out" className={s.logoutIcon} />
                  <span>Log Out</span>
                </Button>
              }
              hoverable
              flowing
            />
          </div>
        </div>
      </aside>
    </SideNavProvider>
  );
};

export default SideNav;
